import { defineStore } from 'pinia'
import { useContextStore } from '~~/stores/context'
import { PROVIDERS } from '#shared/providers'

interface DriveItem {
    id: string
    name: string
    mimeType: string
    isFolder: boolean
    modifiedTime: string | null
    size: number | null
}

interface BreadcrumbEntry {
    id: string
    name: string
}

export const useGoogleDriveStore = defineStore('google-drive', () => {
    const contextStore = useContextStore()

    const items = ref<DriveItem[]>([])
    const loading = ref(false)
    const breadcrumbs = ref<BreadcrumbEntry[]>([])
    const uploadingFiles = ref<string[]>([])
    const sortColumn = ref<'name' | 'modifiedTime'>('name')
    const sortDirection = ref<'asc' | 'desc'>('asc')

    const googleContext = computed(() => contextStore.getContext(PROVIDERS.GOOGLE))

    const rootFolderId = computed(
        () => googleContext.value?.scopes.find((s) => s.name === 'root_path')?.content ?? null,
    )

    // null means "show drives list" (no root_path configured and no breadcrumb)
    const currentFolderId = computed<string | null>(() => {
        if (breadcrumbs.value.length > 0) {
            return breadcrumbs.value[breadcrumbs.value.length - 1].id
        }
        return rootFolderId.value ?? null
    })

    const sortedItems = computed(() => {
        return [...items.value].sort((a, b) => {
            let cmp = 0
            if (sortColumn.value === 'name') {
                cmp = a.name.localeCompare(b.name)
            }
            else {
                const ta = a.modifiedTime ?? ''
                const tb = b.modifiedTime ?? ''
                cmp = ta < tb ? -1 : ta > tb ? 1 : 0
            }
            return sortDirection.value === 'asc' ? cmp : -cmp
        })
    })

    async function fetchFolder(folderId: string | null) {
        loading.value = true
        items.value = []
        await useRequest({
            label: `fetch drive folder ${folderId ?? 'root (drives list)'}`,
            fetch: () => $fetch(`/api/google/drive/list`, {
                method: 'GET',
                params: folderId ? { folderId } : {},
            }),
            evaluate: (data: any) => data.items ?? [],
            store: (data: DriveItem[]) => {
                items.value = data
            },
        })
        loading.value = false
    }

    function navigateInto(item: DriveItem) {
        if (!item.isFolder) {
            return
        }
        breadcrumbs.value = [...breadcrumbs.value, { id: item.id, name: item.name }]
    }

    function navigateToBreadcrumb(index: number) {
        breadcrumbs.value = breadcrumbs.value.slice(0, index + 1)
    }

    function navigateToRoot() {
        breadcrumbs.value = []
    }

    function toggleSort(column: 'name' | 'modifiedTime') {
        if (sortColumn.value === column) {
            sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
        }
        else {
            sortColumn.value = column
            sortDirection.value = 'asc'
        }
    }

    async function uploadFiles(files: File[]) {
        const folderId = currentFolderId.value
        await Promise.all(files.map((file) => uploadFile(file, folderId)))
        await fetchFolder(folderId)
    }

    async function uploadFile(file: File, folderId: string) {
        uploadingFiles.value = [...uploadingFiles.value, file.name]
        try {
            const form = new FormData()
            form.append('folderId', folderId)
            form.append('file', file, file.name)
            await useRequest({
                label: `upload ${file.name}`,
                fetch: () => $fetch('/api/google/drive/upload', {
                    method: 'POST',
                    body: form,
                }),
            })
        }
        finally {
            uploadingFiles.value = uploadingFiles.value.filter((n) => n !== file.name)
        }
    }

    const hasIdentity = computed(() => !!googleContext.value?.identityKey)

    watch(currentFolderId, (id) => {
        if (hasIdentity.value) {
            fetchFolder(id)
        }
    }, { immediate: true })

    watch(rootFolderId, () => {
        breadcrumbs.value = []
    })

    watch(hasIdentity, (has) => {
        if (has) {
            fetchFolder(currentFolderId.value)
        }
        else {
            items.value = []
            breadcrumbs.value = []
        }
    })

    // ── Folder path resolution cache ─────────────────────────────────────────

    const _pathCache = new Map<string, string>()

    async function resolveFolderPath(folderId: string, identityId?: number): Promise<string> {
        const cacheKey = identityId ? `${identityId}:${folderId}` : folderId
        if (_pathCache.has(cacheKey)) {
            return _pathCache.get(cacheKey)!
        }
        const params: Record<string, string> = { folderId }
        if (identityId) {
            params.identityId = String(identityId)
        }
        const data = await useRequest({
            label: `resolve folder path ${folderId}`,
            fetch: () => $fetch('/api/google/drive/folder-info', {
                method: 'GET',
                params,
            }),
            evaluate: (data: any) => data?.path ?? folderId,
        })
        const path = typeof data === 'string' ? data : folderId
        _pathCache.set(cacheKey, path)
        return path
    }

    // ── Folder children (for tree-based pickers) ─────────────────────────────

    async function fetchFolderChildren(folderId: string | null, showFiles = false): Promise<any[]> {
        const params = folderId ? { folderId } : {}
        const result = await useRequest({
            label: `fetch folder children ${folderId ?? 'root'}`,
            fetch: () => $fetch('/api/google/drive/list', {
                method: 'GET',
                params,
            }),
            evaluate: (data: any) => (data?.items ?? []).filter((item: any) => showFiles || item.isFolder),
        })
        return result ?? []
    }

    async function fetchFolderAncestors(folderId: string): Promise<string[]> {
        const result = await useRequest({
            label: `fetch folder ancestors ${folderId}`,
            fetch: () => $fetch('/api/google/drive/folder-info', {
                method: 'GET',
                params: { folderId },
            }),
            evaluate: (data: any) => data?.ancestorIds ?? [],
        })
        return result ?? []
    }

    return {
        items,
        loading,
        breadcrumbs,
        uploadingFiles,
        sortColumn,
        sortDirection,
        rootFolderId,
        currentFolderId,
        hasIdentity,
        sortedItems,
        fetchFolder,
        navigateInto,
        navigateToBreadcrumb,
        navigateToRoot,
        toggleSort,
        resolveFolderPath,
        fetchFolderChildren,
        fetchFolderAncestors,
        uploadFiles,
    }
})
